import { getValueOrDraft } from "components/Beneficiar/utils";
import { featureFlags } from "config/featureFlags";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { selectAllPriceHistory } from "store/priceHistory/priceHistory.selectors";
import { useAppSelector } from "store/store";
import { ChannelCalculationMethod } from "store/types";

import { getCountries } from "../../../../countries";
import { isPriceHistoryEntryPast } from "../utils";
import type { IFinanceGridChannel, IFinanceGridPriceEntry } from "./types";

/** Adapte les données du store (titre courant + historique de prix) au format de la grille. */
export const useFinanceGridChannels = (): IFinanceGridChannel[] => {
    const [t] = useTranslation();
    const { title } = useAppSelector((state) => state.titles);
    const entries = useAppSelector(selectAllPriceHistory);
    const activePeriod = useAppSelector((state) => state.period.activePeriodData);

    return useMemo(() => {
        if (!title) return [];
        const countries = getCountries();
        const today = (activePeriod?.periodEnd ? new Date(activePeriod?.periodEnd) : new Date())
            .toISOString()
            .slice(0, 10);

        const mapEntries = (channelId?: number): IFinanceGridPriceEntry[] => {
            if (!featureFlags.priceHistory) return [];
            return (
                entries
                    .filter((e) => (channelId === undefined ? !e.channelId : e.channelId === channelId))
                    .map((e) => ({
                        id: e.id,
                        price: e.price,
                        currency: e.currency,
                        startDate: e.startDate,
                        endDate: e.endDate,
                        // « passé » relatif au début de la période active : un prix terminé
                        // mais couvrant la période active reste affiché comme actuel
                        status: isPriceHistoryEntryPast(e, activePeriod?.periodStart)
                            ? ("past" as const)
                            : e.startDate && e.startDate.slice(0, 10) > today
                              ? ("future" as const)
                              : ("current" as const),
                        childCurrencies: e.childCurrencies.map((c) => {
                            const country = countries.find((x) => x.code === c.country);
                            return {
                                currency: c.currency,
                                country: c.country,
                                price: c.price,
                                countryName: country?.name ?? c.country,
                                flag: country?.flag ?? "",
                            };
                        }),
                    }))
                    // Chronological, oldest first — same order as the editable grid.
                    .sort((a, b) => (a.startDate ?? "").localeCompare(b.startDate ?? ""))
            );
        };

        // Titles saved before the price-history feature (or by a backend that does
        // not persist it) only carry the legacy price/currency fields — surface
        // them as a single undated period instead of an empty price cell. Their
        // per-country prices live as countryMarket sibling channels.
        const legacyEntries = (
            price?: number | string | null,
            currency?: string | null,
            channelId?: number,
        ): IFinanceGridPriceEntry[] =>
            price != null
                ? [
                      {
                          id: 0,
                          price: Number(price),
                          currency: (currency ?? "EUR") as IFinanceGridPriceEntry["currency"],
                          startDate: null,
                          endDate: null,
                          status: "current" as const,
                          childCurrencies: (title.overriddenChannelsData ?? [])
                              .filter((c) => c.countryMarket && c.channelId === channelId)
                              .map((c) => {
                                  const country = countries.find((x) => x.code === c.countryMarket);
                                  return {
                                      currency: (c.currency ?? "EUR") as IFinanceGridPriceEntry["currency"],
                                      country:
                                          c.countryMarket as IFinanceGridPriceEntry["childCurrencies"][number]["country"],
                                      price: Number(c.price),
                                      countryName: country?.name ?? c.countryMarket ?? "",
                                      flag: country?.flag ?? "",
                                  };
                              }),
                      },
                  ]
                : [];

        const defaultEntries = mapEntries(undefined);
        const channels: IFinanceGridChannel[] = [
            {
                channelName: t("Titles.ByDefault"),
                ean13: null,
                entries: defaultEntries.length > 0 ? defaultEntries : legacyEntries(title.price, title.currency),
            },
        ];

        for (const channel of title.overriddenChannelsData ?? []) {
            if (channel.countryMarket) continue;
            const channelEntries = mapEntries(channel.channelId);
            channels.push({
                channelId: channel.channelId,
                channelName: channel.channelName ?? "—",
                ean13: getValueOrDraft(channel.ean13 ?? "") || null,
                subscriptionCoefficient:
                    channel.calculationMethod === ChannelCalculationMethod.SUBSCRIPTION
                        ? channel.subscriptionCoefficient
                        : undefined,
                entries:
                    channelEntries.length > 0
                        ? channelEntries
                        : legacyEntries(channel.price, channel.currency, channel.channelId),
            });
        }

        return channels;
    }, [activePeriod?.periodStart, entries, t, title]);
};
